import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { UiSettingsIdentifier, UiUserSettingsService } from '../../services/ui-user-settings.service';
import { fetch } from '@nrwl/angular';
import { ProductPageActionTypes, setUserSettingsSuccess } from './products-page.actions';
import * as ProductPageActions from './products-page.actions';
import { map, mergeMap, catchError, withLatestFrom, filter, pluck, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { selectProductsPageUserSettings } from './products-page.selectors';
import { Router } from '@angular/router';
import { routes } from '../../../app.routes';
import { setLatestSearchId } from '../submissions-page/submissions-page.actions';
import { BackendErrorService } from '../../services';
import { translationKeys } from '../../core';

@Injectable()
export class ProductsPageEffects {
  loadSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductPageActionTypes.GetUserSettings),
      fetch({
        run: ({ versionFlag }) => {
          return this.uiUserSettingsService
            .getUiSettingsFor(UiSettingsIdentifier.ProductsTable, versionFlag)
            .pipe(map((settings: Record<string, any>) => ProductPageActions.getUserSettingsSuccess({ settings })));
        },
        onError: (_, error) => {
          this.backendErrorService.handleWarning(error, translationKeys.uiUserSettingsService.msg.warning.restoringSettings);
          return ProductPageActions.getUserSettingsFail({ error });
        }
      })
    );
  });

  setSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductPageActionTypes.SetUserSettings),
      mergeMap(({ settings, versionFlag }) =>
        this.uiUserSettingsService.saveUiSettingsFor(UiSettingsIdentifier.ProductsTable, settings, versionFlag).pipe(
          map(() => ProductPageActions.setUserSettingsSuccess({ settings })),
          catchError((error) => {
            this.backendErrorService.handleWarning(error, translationKeys.uiUserSettingsService.msg.warning.savingSettings);
            return of(ProductPageActions.setUserSettingsFail({ error }));
          })
        )
      )
    );
  });

  setFiltersPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      concatLatestFrom(() => this.store.select(selectProductsPageUserSettings)),
      filter(([action, _]: [any, Record<string, any>]) => action.type === ProductPageActionTypes.SetFiltersPreferences),
      mergeMap(([{ filtersPreferences }, settings]) =>
        this.uiUserSettingsService.saveUiSettingsFor(UiSettingsIdentifier.ProductsTable, { ...settings, filters: filtersPreferences }, '4.1.3').pipe(
          map(() => ProductPageActions.setUserSettingsSuccess({ settings: { ...settings, filters: filtersPreferences }, shouldNavigate: true })),
          catchError((error) => of(ProductPageActions.setUserSettingsFail({ error })))
        )
      )
    );
  });

  navigateToProducts$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ProductPageActionTypes.SetUserSettingsSuccess),
        pluck('shouldNavigate'),
        filter((shouldNavigate) => shouldNavigate),
        tap(() => {
          this.router.navigate([routes.products]);
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly uiUserSettingsService: UiUserSettingsService,
    private readonly backendErrorService: BackendErrorService,
    private readonly store: Store,
    private readonly router: Router
  ) {}
}
